import {activeCityDistrictLayout} from './ssx-city-district-layout.mjs';
import * as T from 'three';
import {createValorianAtmosphere} from '../engine/valorian-atmosphere.mjs';
import {createCity,hasResortBuildingEntrance} from './city.mjs';
import {createEntities} from './entities.mjs';
import {createCityArcadeView} from './ssx-city-arcade-view.mjs';
import {activeCityCourseIds} from './ssx-city-course-gates.mjs';

// Static ground resources are retained separately from the incremental entity
// lifecycle. Entity primitives/materials are reference-counted per entity.
const references = new Map();
function retainResources(root) {
  const resources = new Set();
  root.traverse(object => {
    if (object.geometry) resources.add(object.geometry);
    for (const material of object.material ? (Array.isArray(object.material) ? object.material : [object.material]) : []) resources.add(material);
  });
  for (const resource of resources) references.set(resource, (references.get(resource) ?? 0) + 1);
  return () => {
    for (const resource of resources) {
      const count = (references.get(resource) ?? 1) - 1;
      if (count) references.set(resource, count);
      else { references.delete(resource); resource.dispose(); }
    }
  };
  return view;
}

// Compare only inputs that actually affect the rendered environment. Metadata,
// capabilities, ownership and portal connections must never rebuild the city.
function environmentKey(world) {
  if(world.id==='valoria'){const layout=activeCityDistrictLayout(world);return JSON.stringify(['valoria-city',world.atmosphere??null,hasResortBuildingEntrance(world),layout?[!!layout.westBlockMoved,!!layout.studioFrameRemoved]:null]);}
  const designed=['sunset-court','night-line'].includes(world.theme);
  const bounds=world.bounds??{minX:-32,maxX:32,minZ:-38,maxZ:27};
  const rectangle=designed&&Math.hypot(Math.max(Math.abs(bounds.minX),Math.abs(bounds.maxX)),Math.max(Math.abs(bounds.minZ),Math.abs(bounds.maxZ)))<=76;
  return JSON.stringify([designed?'designed':world.theme==='moonwater'?'moonwater':'default',rectangle?[bounds.minX,bounds.maxX,bounds.minZ,bounds.maxZ]:null,world.atmosphere??null]);
}

/** The same world composition is used for the active view and portal cameras.
 * No renderer, canvas, transport, player avatar or second simulation is created.
 */
export function createWorldScene(world, {time = 0, shadowMapSize = 1024} = {}) {
  const scene = new T.Scene();
  scene.name = `World · ${world.name || world.id}`;
  scene.background = new T.Color('#181728');
  const settings=world.atmosphere??{};
  scene.add(new T.HemisphereLight('#b8c0f5', '#30243e', settings.ambientIntensity??1.6));
  const sun = new T.DirectionalLight('#ffd2bc', settings.sunIntensity??2.8);
  sun.position.set(-40, 65, 40);
  sun.castShadow = true;
  sun.shadow.mapSize.set(shadowMapSize, shadowMapSize);
  Object.assign(sun.shadow.camera, {left: -55, right: 55, top: 55, bottom: -55});
  sun.shadow.bias = -.0003;
  sun.shadow.normalBias = .03;
  scene.add(sun);
  const rim = new T.DirectionalLight('#a49fff', 1.3);
  rim.position.set(20, 25, -35);
  scene.add(rim);
  const root = new T.Group();
  root.name = `World content · ${world.id}`;
  scene.add(root);
  const isCity = world.id === 'valoria';
  const continuity = {time};
  const atmosphere = createValorianAtmosphere(T, scene, {
    background: '#302245', backgroundGridBounds: isCity ? [-70, -70, 70, 70] : [-35, -55, 35, 35],
    backgroundGridY: -.14,
    atmosphere: {skyColor: '#685c91', horizonColor: '#bd8997', stars: true, ...settings},
    authoredPalette: world.atmosphere!=null,
  }, continuity);
  let city = null;
  const groundRoot = new T.Group();
  if (isCity) { city = createCity(T, {world}); root.add(city.group); }
  else {
    const designedPlace = ['sunset-court','night-line'].includes(world.theme);
    const bounds=world.bounds??{minX:-32,maxX:32,minZ:-38,maxZ:27};
    const rectangularGround=designedPlace&&Math.hypot(Math.max(Math.abs(bounds.minX),Math.abs(bounds.maxX)),Math.max(Math.abs(bounds.minZ),Math.abs(bounds.maxZ)))<=76;
    const geometry=rectangularGround?new T.PlaneGeometry(bounds.maxX-bounds.minX,bounds.maxZ-bounds.minZ):new T.CircleGeometry(76,96);
    const ground = new T.Mesh(geometry,new T.MeshStandardMaterial({
      color: settings.groundColor??(designedPlace?'#48445f':world.theme==='moonwater'?'#2b3448':'#393148'),roughness:.88,metalness:.08,
    }));
    ground.rotation.x=-Math.PI/2;
    ground.position.set(rectangularGround?(bounds.maxX+bounds.minX)/2:0,-.075,rectangularGround?(bounds.maxZ+bounds.minZ)/2:0);
    ground.receiveShadow=true;groundRoot.add(ground);
    if(!designedPlace){
      const ringMaterial = new T.MeshBasicMaterial({color:'#635172',side:T.DoubleSide,transparent:true,opacity:.6});
      for(let radius=10;radius<70;radius+=10){
        const ring=new T.Mesh(new T.RingGeometry(radius,radius+.035,128),ringMaterial);
        ring.rotation.x=-Math.PI/2;ring.position.y=-.06;groundRoot.add(ring);
      }
    }
    root.add(groundRoot);
  }
  const arcadeCourses=activeCityCourseIds;
  const arcade=isCity?createCityArcadeView(root,{courseIds:arcadeCourses(world),layout:activeCityDistrictLayout(world)}):null;
  const entities = createEntities(world, {time});
  root.add(entities.root);
  // Offscreen targets are linear HDR; the portal surface applies the final
  // display transform. Apply that SAME transform to every directly drawn world
  // material, including the legacy sky/grid shaders and emissive city signs.
  // Otherwise toneMapped:false materials change brightness at camera crossing.
  scene.traverse(object => {
    for (const material of object.material ? (Array.isArray(object.material) ? object.material : [object.material]) : []) {
      let changed = !material.toneMapped;
      material.toneMapped = true;
      if (material.isShaderMaterial && !material.fragmentShader.includes('#include <tonemapping_fragment>')) {
        changed = true;
        material.fragmentShader = material.fragmentShader.replace('#include <colorspace_fragment>', '#include <tonemapping_fragment>\n#include <colorspace_fragment>');
      }
      if (changed) material.needsUpdate = true;
    }
  });
  const releaseGround = retainResources(groundRoot), initialEnvironment = environmentKey(world);
  let disposed = false, lastUpdateTime = null, elapsedTime = time;
  const view = {
    world, scene, root, city, entities, atmosphere,
    getElapsed() { return elapsedTime; },
    restoreAnimationFrom(previous) {
      if (!previous?.entities) return;
      elapsedTime = previous.getElapsed?.() ?? elapsedTime;
      lastUpdateTime = elapsedTime;
      atmosphere.sky.material.uniforms.time.value = elapsedTime;
      city?.update(0, elapsedTime); entities.update(0, elapsedTime);
      for (const [id, object] of entities.objects) {
        const old = previous.entities.objects.get(id);
        if (old) object.group.rotation.y = (object.snapshot.rotation?.y ?? 0) + old.group.rotation.y - (old.snapshot.rotation?.y ?? 0);
      }
    },
    canUpdateWorld(next) { return !disposed && next.id === view.world.id && environmentKey(next) === initialEnvironment; },
    updateWorld(next, options = {}) {
      if (!view.canUpdateWorld(next)) return false;
      const changes = entities.updateWorld(next, options);
      view.world = next;
      arcade?.update(arcadeCourses(next),activeCityDistrictLayout(next));
      view.lastWorldChanges = changes;
      scene.name = `World · ${next.name || next.id}`;
      // City doorframes are fixed architectural geometry; only their connection
      // metadata changes. Do not rebuild thousands of instances for a new link.
      for (const landmark of city?.landmarks ?? []) {
        const connection = next.portals?.find(portal => portal.id === landmark.id || Math.hypot(portal.position.x-landmark.position[0],portal.position.z-landmark.position[2])<.5);
        landmark.active = !!connection;
        if (connection) { landmark.name = connection.label || landmark.name; landmark.targetWorldId = connection.targetWorldId; }
      }
      return true;
    },
    hasPortalFrame(portal) {
      return !!city?.landmarks?.some(landmark => {
        const p = landmark.position;
        return Math.hypot((p.x ?? p[0]) - portal.position.x, (p.y ?? p[1]) - portal.position.y, (p.z ?? p[2]) - portal.position.z) < .6 && Math.abs(Math.sin((landmark.yaw ?? 0) - (portal.yaw ?? 0))) < .01 && (portal.width ?? 3.2) === landmark.width && (portal.height ?? 3.4) === landmark.height && (portal.aperture ?? 'rectangle') === landmark.aperture;
      });
    },
    update(dt, elapsed) {
      if (disposed || (Number.isFinite(elapsed) && elapsed === lastUpdateTime)) return;
      lastUpdateTime = elapsed;
      elapsedTime = Number.isFinite(elapsed) ? elapsed : elapsedTime + Math.max(0, dt);
      // Every view shares the supplied absolute clock, including freshly staged
      // worlds, instead of restarting sky motion when a revision arrives.
      atmosphere.sky.material.uniforms.time.value = elapsedTime - Math.min(.1, Math.max(0, dt));
      atmosphere.update(dt);
      city?.update(dt, elapsed);
      entities.update(dt, elapsed);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      arcade?.dispose();
      city?.dispose();
      atmosphere.dispose();
      entities.dispose();
      releaseGround();
      sun.shadow.dispose();
      root.removeFromParent();
      scene.clear();
    },
  };
  return view;
}
